import {
	GridColDef,
	GridFilterModel,
	GridPaginationModel,
	GridSortModel,
	getGridDateOperators,
	getGridNumericOperators,
} from '@mui/x-data-grid-premium';
import { isEmptyGridFilterValue } from '@parkchirp/ui-library';
import React, { Dispatch, SetStateAction } from 'react';

import type { SetShowDialog } from '../../types';

import { convertMuiFilterForV2Api } from '../../utils';

export const defaultFilters = [];

export const defaultOrder = {
	field: 'createdAt',
	sort: 'asc',
};

export const OnSortModelChange =
	({
		setPaginationModel,
		setSortOrder,
	}: {
		setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
		setSortOrder: Dispatch<
			SetStateAction<{
				field: string;
				sort: string;
			}>
		>;
	}) =>
	(sortModel: GridSortModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));
		if (!sortModel.length) {
			return setSortOrder({ field: 'createdAt', sort: 'DESC' });
		}

		const { field, sort } = sortModel[0];

		return setSortOrder({ field, sort });
	};

export const OnFilterModelChange =
	({
		setPaginationModel,
		setFilters,
	}: {
		setPaginationModel: Dispatch<SetStateAction<GridPaginationModel>>;
		setFilters: Dispatch<
			SetStateAction<
				{
					propertyName: string;
					op: string;
					value: string;
				}[]
			>
		>;
	}) =>
	(filterModel: GridFilterModel) => {
		setPaginationModel((prev) => ({ ...prev, page: 0 }));

		const { items: filterItems } = filterModel;

		if (!filterItems.length || filterItems.every((i) => !i.value)) {
			setFilters(defaultFilters);
			return;
		}

		const queryFilters = filterItems
			.map((filter) => {
				const { field, operator, value } = filter;

				if (isEmptyGridFilterValue(value) && !['isEmpty', 'isNotEmpty'].includes(operator)) return [];

				const op = convertMuiFilterForV2Api(operator, value);

				return {
					propertyName: field,
					op: op.op,
					value: value ?? null,
				};
			})
			.flat();

		setFilters(queryFilters);
	};

export function getWaitlistParkersTableColumns({
	parkerDetailModal,
	setShowDialog,
}: {
	parkerDetailModal: ((props: any) => React.ReactElement) | null;
	setShowDialog: SetShowDialog;
}): GridColDef[] {
	const style = {
		background: 'none',
		border: 0,
		color: 'blue',
		fontSize: 'inherit',
		cursor: 'pointer',
	};

	const numericOperators = getGridNumericOperators();
	const dateOperators = getGridDateOperators();

	const columnDefs: GridColDef[] = [
		{
			field: 'createdAt',
			headerName: 'Date Added',
			groupable: false,
			filterOperators: dateOperators,
			sortable: false,
		},
		{
			field: 'lastName',
			headerName: 'Last Name',
			groupable: false,
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						if (!parkerDetailModal) return;
						setShowDialog(
							parkerDetailModal({
								parkerId: params.row.id,
								parkerFirstName: params.row.firstName,
								parkerLastName: params.row.lastName,
								parkerAccountId: params.row.accountId,
								parkerSubscriptionName: params.row.subscriptionName,
								facilityId: params.row.facilityId,
								subscriptionId: params.row.subscriptionId,
								parkerFacilityName: params.row.facilityName,
								parkerFacilityNumber: params.row.facilityNumber,
								handleClose: () => {
									setShowDialog(null);
								},
							}),
						);
					}}
					data-cy='last-name-link'
				>
					{params.row.lastName}
				</a>
			),
		},
		{
			field: 'firstName',
			headerName: 'First Name',
			groupable: false,
			renderCell: (params) => (
				<a
					style={style}
					onClick={() => {
						if (!parkerDetailModal) return;
						setShowDialog(
							parkerDetailModal({
								parkerId: params.row.id,
								parkerFirstName: params.row.firstName,
								parkerLastName: params.row.lastName,
								parkerAccountId: params.row.subscriptionAccountId,
								parkerSubscriptionName: params.row.subscriptionName,
								facilityId: params.row.facilityId,
								subscriptionId: params.row.subscriptionId,
								parkerFacilityName: params.row.facilityName,
								parkerFacilityNumber: params.row.facilityNumber,
								handleClose: () => {
									setShowDialog(null);
								},
							}),
						);
					}}
					data-cy='first-name-link'
				>
					{params.row.firstName}
				</a>
			),
		},
		{
			field: 'waitlistStartDate',
			headerName: 'Desired Start Date',
			groupable: false,
			filterOperators: dateOperators,
			sortable: false,
		},
		{
			field: 'waitlistRateName',
			headerName: 'Waitlist Rate Name',
			groupable: false,
		},
		{
			field: 'facilityName',
			headerName: 'Facility Name',
			groupable: false,
		},
		{
			field: 'facilityNumber',
			headerName: 'Facility Number',
			groupable: false,
			filterOperators: numericOperators,
		},
		{
			field: 'subscriptionName',
			headerName: 'Subscription Name',
			groupable: false,
		},
		{
			field: 'accountId',
			headerName: 'Account ID',
			filterOperators: numericOperators,
			groupable: false,
			renderCell: (params) => `AC-${params.row.accountId}`,
		},
		{
			field: 'readableId',
			headerName: 'Sub. ID',
			filterOperators: numericOperators,
			groupable: false,
		},
		{
			field: 'payorType',
			headerName: 'Payor Type',
			groupable: false,
			type: 'singleSelect' as const,
			valueOptions: [
				{ value: 'Individual', label: 'Individual' },
				{ value: 'Business', label: 'Business' },
			],
		},
		{
			field: 'email',
			headerName: 'Email',
			groupable: false,
		},
		{
			field: 'phone',
			headerName: 'Phone',
			groupable: false,
		},
		{
			field: 'address',
			headerName: 'Address',
			groupable: false,
		},
		{
			field: 'city',
			headerName: 'City',
			groupable: false,
		},
		{
			field: 'state',
			headerName: 'State',
			groupable: false,
		},
		{
			field: 'zip',
			headerName: 'Zip',
			groupable: false,
		},
	];
	return columnDefs.map((colDef) => ({
		...colDef,
		groupable: false,
	}));
}
